// Create singleton instance
import { SERVER_VALIDATION_TYPES } from '../../components/v2/types/schema';
import { HttpClient } from '../http';

/**
 * Validation Service for server-side validations
 */
class ValidationService {
  constructor(config = {}) {
    this.httpClient = new HttpClient({
      baseUrl: config.baseUrl || 'https://app.legalstream.org/api/forms/validate',
    //   baseUrl: 'http://localhost:8081/api/forms/validate',
      onRequest: this.logRequest.bind(this),
      onResponse: this.logResponse.bind(this),
      onError: this.logError.bind(this),
    });
  }

  logRequest(config) {
    console.debug('Validation Request:', {
      url: config.url,
      method: config.method,
      data: config.data,
    });
  }

  logResponse(response) {
    console.debug('Validation Response:', {
      status: response.status,
      data: response.data,
    });
  }

  logError(error) {
    console.error('Validation Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
  }

  /**
   * Fetch all available validation types from server
   */
  async fetchValidationTypes() {
    console.group('Validation Service: Fetching validation types');
    try {
      console.info('Requesting validation types from server...');
      
      // Make GET request to get available validation types
      const response = await this.httpClient.get('');
      
      console.debug('Raw response:', response);
      
      // The response should be: { validations: ["CLIENT_ACCOUNT_NUMBER_EXISTS", "LS_ACCOUNT_NUMBER_EXISTS"] }
      const validationTypes = response?.validations || [];
      
      console.info(`Available validation types: ${validationTypes.length}`);
      console.debug('Types:', validationTypes);
      
      return validationTypes;
    } catch (error) {
      console.error('Failed to fetch validation types:', error);
      console.error('Error details:', error.message);
      return [];
    } finally {
      console.groupEnd();
    }
  }

  /**
   * Validate a single value against server-side validation
   */
  async validateServerSide(validationType, value, context = {}) {
    console.group(`Validation Service: Validating ${validationType}`);
    
    try {
      const response = await this.httpClient.post('', {
        validationType,
        value,
        context
      });

      console.debug('Validation response:', response);

      return {
        valid: response.valid || false,
        errorCode: response.errorCode,
        message: response.message || 'Validation failed'
      };
    } catch (error) {
      console.error(`Validation error for ${validationType}:`, error);
      console.error('Error details:', error.message);

      return {
        valid: false,
        errorCode: 'SERVER_ERROR',
        message: 'Server validation error. Please try again.'
      };
    } finally {
      console.groupEnd();
    }
  }

  /**
   * Process all server-side validations for a field
   */
  async processServerValidations(field, value, formData = {}) {
    if (!field?.serverValidations) return null;
    
    console.group(`Validation Service: Processing server validations for ${field.name}`);
    console.debug('Field:', field.name, 'Value:', value);
    
    const errors = [];
    
    for (const validation of field.serverValidations) {
      const { type, message, enabled = true } = validation;
      
      // Skip disabled validations
      if (!enabled) continue;
      
      // Skip if not a server-side validation type
      if (type !== SERVER_VALIDATION_TYPES.CLIENT_ACCOUNT_NUMBER_EXISTS && 
          type !== SERVER_VALIDATION_TYPES.LS_ACCOUNT_NUMBER_EXISTS) {
        continue;
      }
      
      // Skip if value is empty (let client-side required handle this)
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        console.debug(`Skipping ${type} validation - empty value`);
        continue;
      }
      
      console.info(`Processing ${type} validation...`);
      
      try {
        const result = await this.validateServerSide(type, value, formData);
        
        if (!result.valid) {
          const errorMessage = message || result.message || `Validation failed for ${field.label || field.name}`;
          errors.push(errorMessage);
          console.warn(`Validation failed for ${type}:`, errorMessage);
        } else {
          console.info(`Validation passed for ${type}`);
        }
      } catch (error) {
        const errorMessage = 'Server validation error. Please try again.';
        errors.push(errorMessage);
        console.error(`Server error for ${type}:`, error);
      }
    }
    
    const finalError = errors.length > 0 ? errors[0] : null;
    
    if (finalError) {
      console.error(`Server validations failed for ${field.name}:`, finalError);
    } else {
      console.info(`All server validations passed for ${field.name}`);
    }
    
    console.groupEnd();
    return finalError;
  }
}

// Create singleton instance
const validationService = new ValidationService();

export const fetchValidationTypes = () => validationService.fetchValidationTypes();
export const validateServerSide = (type, value, context) => validationService.validateServerSide(type, value, context);
export const processServerValidations = (field, value, formData) => validationService.processServerValidations(field, value, formData);

export { ValidationService };